import { motion, useReducedMotion } from 'framer-motion'
import ChainIcon from './ChainIcon'
import SealEmblem from './SealEmblem'
import { ON_CHAIN_SECTION_ID, getNetworkLabel } from './cardFields'
const EASE = [0.16, 1, 0.3, 1]
function AuthenticationSeal({ tokenId, chainId, chainName, ready = true, onActivate }) {
  const reduceMotion = useReducedMotion()
  const plate = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 6 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: reduceMotion ? 0.2 : 0.5,
        delay: reduceMotion ? 0 : 0.1,
        ease: EASE,
      },
    },
  }
  const scrollToProof = () => {
    if (onActivate) {
      onActivate()
      return
    }
    document.getElementById(ON_CHAIN_SECTION_ID)?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    })
  }
  return (
    <motion.button
      type="button"
      onClick={scrollToProof}
      variants={plate}
      initial="hidden"
      animate={ready ? 'show' : 'hidden'}
      aria-label={`Verified on ${chainName}${tokenId != null ? `, token ${tokenId}` : ''}. View the on-chain proof.`}
      style={{
        background:
          'linear-gradient(180deg, rgba(180,156,245,0.10) 0%, rgba(18,16,24,0.78) 55%, rgba(18,16,24,0.78) 100%)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07), 0 14px 36px -16px rgba(180,156,245,0.32)',
      }}
      className="group relative block w-full overflow-hidden rounded-2xl border border-m1-line text-left transition-colors hover:border-m1-violet/50"
    >
      {/* Light source: a soft glow behind the seal. */}
      <span
        className="pointer-events-none absolute -left-8 -top-12 h-36 w-52 rounded-full bg-m1-violet/25 blur-3xl"
        aria-hidden="true"
      />
      {/* One pass of light across the plate once it has been stamped. */}
      {!reduceMotion && (
        <motion.span
          className="pointer-events-none absolute inset-y-0 left-0 w-1/3 -skew-x-12"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.10), transparent)',
          }}
          initial={{ x: '-120%', opacity: 0 }}
          animate={ready ? { x: '420%', opacity: [0, 1, 1, 0] } : { x: '-120%', opacity: 0 }}
          transition={{ duration: 1.2, delay: 0.7, ease: 'easeInOut' }}
          aria-hidden="true"
        />
      )}

      <span className="relative flex items-center gap-4 px-4 py-3 sm:py-4">
        <SealEmblem className="h-[52px] w-[52px] shrink-0 sm:h-[60px] sm:w-[60px]" />

        <span className="min-w-0 flex-1">
          <span className="block text-[13px] uppercase tracking-[0.18em] text-m1-muted">
            Verified on
          </span>
          <span className="mt-1.5 flex items-center gap-2 font-m1-display text-[26px] leading-none text-m1-ink">
            <ChainIcon chainId={chainId} className="h-6 w-6" />
            Polygon
          </span>
        </span>

        <i
          className="ri-arrow-down-s-line shrink-0 text-2xl leading-none text-m1-muted transition-colors group-hover:text-m1-violet"
          aria-hidden="true"
        />
      </span>

      <span className="relative flex items-center justify-between gap-3 border-t border-m1-line-soft px-4 py-2.5 text-[14px] text-m1-ink-soft sm:py-3">
        <span className="font-m1-mono">
          {tokenId != null ? `Token #${tokenId}` : 'On-chain record'}
        </span>
        <span className="text-m1-muted">{getNetworkLabel(chainId)}</span>
      </span>
    </motion.button>
  )
}
export { AuthenticationSeal as default }
